import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/pages/home/components/Navbar";
import ProfileHeader from "./components/ProfileHeader";
import StatsCards from "./components/StatsCards";
import MyArtistXP from "./components/MyArtistXP";
import MyBadges from "./components/MyBadges";
import MyQuests from "./components/MyQuests";
import MyRewards from "./components/MyRewards";
import EditProfileModal from "./components/EditProfileModal";

type TabId = "overview" | "quests" | "rewards" | "badges";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "개요", icon: "ri-layout-grid-line" },
  { id: "quests", label: "캠페인", icon: "ri-film-line" },
  { id: "rewards", label: "리워드", icon: "ri-gift-line" },
  { id: "badges", label: "배지", icon: "ri-award-line" },
];

export default function MyPage() {
  const { isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 mx-auto mb-4">
              <i className="ri-user-line text-white/30 text-2xl" />
            </div>
            <h2 className="text-white text-xl font-bold mb-2">로그인이 필요해요</h2>
            <p className="text-white/40 text-sm mb-6">마이페이지를 보려면 먼저 로그인하세요</p>
            <button
              onClick={() => navigate("/login", { state: { from: "/mypage" } })}
              className="px-6 py-3 rounded-xl font-bold text-sm text-white cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(135deg, #ff6eb4, #4fc3f7)" }}
            >
              <i className="ri-login-circle-line mr-2" />
              로그인하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    setShowLogoutConfirm(false);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0 space-y-4">
            <ProfileHeader onEditProfile={() => setShowEditModal(true)} />

            {/* Quick menu */}
            <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
              <nav>
                {TABS.map((tab, i) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-medium transition-all cursor-pointer whitespace-nowrap text-left ${
                      activeTab === tab.id
                        ? "bg-white/8 text-white"
                        : "text-white/50 hover:text-white/80 hover:bg-white/4"
                    } ${i > 0 ? "border-t border-white/5" : ""}`}
                  >
                    <div className={`w-5 h-5 flex items-center justify-center`}>
                      <i
                        className={`${tab.icon} text-sm`}
                        style={{ color: activeTab === tab.id ? "#ff6eb4" : undefined }}
                      />
                    </div>
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#ff6eb4]" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Logout */}
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/8 text-white/40 text-sm font-medium hover:border-white/20 hover:text-white/60 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line" />
              로그아웃
            </button>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {activeTab === "overview" && (
              <div className="space-y-6">
                <StatsCards />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                    <MyArtistXP />
                  </div>
                  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                    <MyBadges />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "quests" && (
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <MyQuests />
              </div>
            )}

            {activeTab === "rewards" && (
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <MyRewards />
              </div>
            )}

            {activeTab === "badges" && (
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <MyBadges />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && <EditProfileModal onClose={() => setShowEditModal(false)} />}

      {/* Logout Confirm */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) => e.target === e.currentTarget && setShowLogoutConfirm(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowLogoutConfirm(false)} />
          <div className="relative bg-[#0f0f1a] border border-white/15 rounded-2xl p-6 w-full max-w-sm z-10 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 mx-auto mb-4">
              <i className="ri-logout-box-line text-white/50 text-xl" />
            </div>
            <h3 className="text-white font-bold text-base mb-2">로그아웃 하시겠어요?</h3>
            <p className="text-white/40 text-sm mb-6">다음에 또 만나요!</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 rounded-xl border border-white/10 text-white/50 text-sm font-medium hover:text-white/70 cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-3 rounded-xl bg-white/8 border border-white/15 text-white text-sm font-bold hover:bg-white/12 cursor-pointer whitespace-nowrap transition-all"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
